import React, { useState, useEffect } from 'react';
import { getSavedGroups, deleteGroup, SavedGroup } from '../utils/groupStorage';

interface SettingsProps {
  workingHoursStart: string;
  workingHoursEnd: string;
  detectedTimeZone: string; // Read-only, auto-detected from browser
  includeAttendees: boolean;
  onWorkingHoursStartChange: (value: string) => void;
  onWorkingHoursEndChange: (value: string) => void;
  onIncludeAttendeesChange: (value: boolean) => void;
  onClose: () => void;
}

export const Settings: React.FC<SettingsProps> = ({
  workingHoursStart,
  workingHoursEnd,
  detectedTimeZone,
  includeAttendees,
  onWorkingHoursStartChange,
  onWorkingHoursEndChange,
  onIncludeAttendeesChange,
  onClose,
}) => {
  const [groups, setGroups] = useState<SavedGroup[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = () => {
    const savedGroups = getSavedGroups();
    setGroups(savedGroups);
  };

  const handleDeleteGroup = (id: string) => {
    setDeleteConfirm(id);
  };

  const handleConfirmDelete = async (id: string) => {
    try {
      await deleteGroup(id);
      loadGroups();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete group:', error);
      setDeleteConfirm(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2 className="settings-title">Settings</h2>
        <button className="close-settings-btn" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="settings-content">
        {/* Working Hours */}
        <div className="settings-section">
          <label className="section-label">Working Hours</label>
          <div className="working-hours">
            <div className="constraint-group">
              <label>Start Time</label>
              <input
                type="time"
                className="time-input"
                value={workingHoursStart}
                onChange={(e) => onWorkingHoursStartChange(e.target.value)}
              />
            </div>
            <div className="constraint-group">
              <label>End Time</label>
              <input
                type="time"
                className="time-input"
                value={workingHoursEnd}
                onChange={(e) => onWorkingHoursEndChange(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Detected Timezone (read-only) */}
        <div className="settings-section">
          <label className="section-label">Your Time Zone</label>
          <div className="detected-timezone">
            {detectedTimeZone}
            <span className="timezone-note">(auto-detected from your computer)</span>
          </div>
        </div>

        {/* Email Options */}
        <div className="settings-section">
          <label className="section-label">Email Options</label>
          <div className="settings-checkbox-group">
            <input
              type="checkbox"
              id="include-attendees"
              className="settings-checkbox"
              checked={includeAttendees}
              onChange={(e) => onIncludeAttendeesChange(e.target.checked)}
            />
            <label htmlFor="include-attendees" className="settings-checkbox-label">
              Include list of attendees when inserting times into email
            </label>
          </div>
        </div>

        {/* Manage Groups */}
        <div className="settings-section">
          <label className="section-label">Manage Saved Groups</label>
          {groups.length > 0 ? (
            <div className="groups-management-list">
              {groups.map((group) => (
                <div key={group.id}>
                  <div className="group-management-item">
                    <div className="group-info">
                      <div className="group-name">{group.name}</div>
                      <div className="group-count">
                        {group.people.length} {group.people.length === 1 ? 'person' : 'people'}
                      </div>
                    </div>
                    <button
                      className="delete-group-btn"
                      onClick={() => handleDeleteGroup(group.id)}
                      aria-label={`Delete ${group.name}`}
                    >
                      ×
                    </button>
                  </div>

                  {deleteConfirm === group.id && (
                    <div className="delete-confirm">
                      <span className="delete-confirm-text">Delete this group?</span>
                      <div className="delete-confirm-buttons">
                        <button
                          className="dialog-btn primary"
                          onClick={() => handleConfirmDelete(group.id)}
                        >
                          Delete
                        </button>
                        <button
                          className="dialog-btn secondary"
                          onClick={handleCancelDelete}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-groups">No saved groups</div>
          )}
        </div>
      </div>
    </div>
  );
};
